import type { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import {
  Configuration,
  ElevationApi,
  type EnumAccessType,
  ResponseError,
} from '@internal/backstage-plugin-neo4j-permission-elevation-common';
import { BackendAuthApi } from '@internal/plugin-neo4j-auth-common';

import { PERMISSION_ELEVATION_API_BASE_URL } from './const';
import { PermissionElevationApiError } from './errors';
import {
  type ElevationRequest,
  type ElevationResult,
  type PermissionElevationApi,
} from './types';
import { toElevationResult } from './utils/elevationRecords';

export class PermissionElevationRestApi implements PermissionElevationApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly backendAuthApi: BackendAuthApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    backendAuthApi: BackendAuthApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.backendAuthApi = options.backendAuthApi;
  }

  async getAccessTypes(): Promise<EnumAccessType[]> {
    try {
      const api = await this.getElevationApi();
      const access = await api.v1UserAccessGet();

      return access.allowedLevels ?? [];
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async elevate(request: ElevationRequest): Promise<ElevationResult> {
    try {
      const api = await this.getElevationApi();
      const response = await api.v1ElevationsPost({ elevation: request });

      return toElevationResult(response);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async deelevate(accessType: EnumAccessType): Promise<void> {
    try {
      const api = await this.getElevationApi();
      await api.v1ElevationsAccessTypeDelete({ accessType });
    } catch (error) {
      await this.handleSdkError(error);
    }
  }

  private async getElevationApi(): Promise<ElevationApi> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const token = await this.backendAuthApi.getToken();

    return new ElevationApi(
      new Configuration({
        basePath: `${baseUrl}${PERMISSION_ELEVATION_API_BASE_URL}`,
        fetchApi: this.fetchApi.fetch,
        apiKey: token ? `Bearer ${token}` : undefined,
      }),
    );
  }

  private async handleSdkError(error: unknown): Promise<never> {
    if (error instanceof ResponseError) {
      const response = error.response;
      let message = error.message || response.statusText;

      try {
        const json = await response.json();
        if (typeof json.message === 'string') {
          message = json.message;
        }
      } catch {
        // Failed to parse error response, use default message
      }

      throw new PermissionElevationApiError(message, response.status);
    }

    if (error instanceof Error) {
      throw new PermissionElevationApiError(error.message, 400);
    }

    throw new PermissionElevationApiError('Unknown error', 400);
  }
}
